/**
 * Decorative SVG / pattern utility components – used across pages
 * to add tasteful "rare" design elements without overwhelming content.
 */

export const SectionDivider = ({ flip = false, color = "#0B192C", className = "" }) => (
  <div className={`w-full overflow-hidden leading-none ${className}`}>
    <svg
      viewBox="0 0 1440 80"
      preserveAspectRatio="none"
      className={`w-full h-12 md:h-20 ${flip ? "rotate-180" : ""}`}
      aria-hidden="true"
    >
      <path
        d="M0,40 C240,80 480,0 720,32 C960,64 1200,8 1440,40 L1440,80 L0,80 Z"
        fill={color}
      />
    </svg>
  </div>
);

export const DotGrid = ({
  className = "",
  size = 14,
  color = "rgba(234,88,12,0.45)",
}) => (
  <div
    aria-hidden="true"
    className={className}
    style={{
      backgroundImage: `radial-gradient(${color} 1px, transparent 1.5px)`,
      backgroundSize: `${size}px ${size}px`,
    }}
  />
);

export const CornerBracket = ({ position = "tl", color = "#EA580C" }) => {
  const styles = {
    tl: "top-3 left-3 border-t-2 border-l-2",
    tr: "top-3 right-3 border-t-2 border-r-2",
    bl: "bottom-3 left-3 border-b-2 border-l-2",
    br: "bottom-3 right-3 border-b-2 border-r-2",
  };
  return (
    <span
      aria-hidden="true"
      className={`absolute w-6 h-6 ${styles[position]}`}
      style={{ borderColor: color }}
    />
  );
};

export const DecoStamp = ({ text = "FORTUNA · 2003", className = "" }) => (
  <div
    aria-hidden="true"
    className={`spin-slow w-32 h-32 md:w-40 md:h-40 relative ${className}`}
  >
    <svg viewBox="0 0 200 200" className="w-full h-full">
      <defs>
        <path
          id="circle"
          d="M 100, 100 m -75, 0 a 75,75 0 1,1 150,0 a 75,75 0 1,1 -150,0"
        />
      </defs>
      <text fill="#EA580C" fontSize="15" fontFamily="Outfit, sans-serif" fontWeight="700">
        <textPath
          href="#circle"
          startOffset="0"
          textLength="471"
          lengthAdjust="spacingAndGlyphs"
        >
          {`${text} \u2022 ${text} \u2022 `}
        </textPath>
      </text>
    </svg>
    <span className="absolute inset-0 flex items-center justify-center text-[#EA580C] text-xs font-heading font-extrabold tracking-widest">
      20+ ÉV
    </span>
  </div>
);

export const FloatingShape = ({
  className = "",
  variant = "circle",
  color = "#EA580C",
}) => {
  if (variant === "ring")
    return (
      <span
        aria-hidden="true"
        className={`absolute rounded-full border-2 ${className}`}
        style={{ borderColor: color }}
      />
    );
  if (variant === "square")
    return (
      <span
        aria-hidden="true"
        className={`absolute ${className} rotate-12`}
        style={{ background: color, opacity: 0.1 }}
      />
    );
  return (
    <span
      aria-hidden="true"
      className={`absolute rounded-full ${className}`}
      style={{ background: color, opacity: 0.18, filter: "blur(50px)" }}
    />
  );
};

export const NumberWatermark = ({ n = "01", className = "" }) => (
  <span
    aria-hidden="true"
    className={`absolute font-heading font-extrabold text-[#0B192C]/[0.04] leading-none select-none pointer-events-none ${className}`}
    style={{ fontSize: "clamp(140px, 20vw, 320px)", letterSpacing: "-0.04em" }}
  >
    {n}
  </span>
);

export const DiagonalLines = ({ className = "", opacity = 0.04 }) => (
  <div
    aria-hidden="true"
    className={`pointer-events-none ${className}`}
    style={{
      backgroundImage:
        "repeating-linear-gradient(45deg, currentColor 0 1px, transparent 1px 18px)",
      opacity,
    }}
  />
);
